import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

const SLIDES = [
  "Fahrerkarten",
  "Umleitungen",
  "Betriebsinfos",
  "Komunikation und Zusammenarbeit",
  "Schulungen",
  "Fahrpläne",
  "Firmenhandys Infos"
];

const SPEED = 0.015; // px/ms, slow speed
const GAP = 10;
const FALLBACK_WIDTH = 120;

export const TextSlider = () => {
  const containerRef = useRef(null);
  const textRefs = useRef(SLIDES.map(() => React.createRef()));

  const [textWidths, setTextWidths] = useState(SLIDES.map(() => FALLBACK_WIDTH));
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);

  // Measure text widths and container width
  useEffect(() => {
    const measure = () => {
      const widths = SLIDES.map((_, i) => {
        const el = textRefs.current[i].current;
        return el ? el.offsetWidth : FALLBACK_WIDTH;
      });
      setTextWidths(widths);
      const c = containerRef.current;
      setContainerWidth(c ? c.offsetWidth : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Calculate how many times to repeat the slides to fill the row (plus one extra for seamlessness)
  function getFullRowData() {
    const singleRowWidth = SLIDES.reduce((sum, _, i) => sum + (textWidths[i] || FALLBACK_WIDTH) + GAP, 0);
    if (containerWidth === 0 || singleRowWidth === 0) return [];
    const repeat = Math.ceil(containerWidth / singleRowWidth) + 2; // +2 for seamlessness
    let arr = [];
    for (let i = 0; i < repeat; i++) {
      for (let s = 0; s < SLIDES.length; s++) {
        arr.push({ text: SLIDES[s], key: `${s}-${i}` });
      }
    }
    return arr;
  }

  // Set initial offset so that the row is filled from the left (no gap)
  useEffect(() => {
    const singleRowWidth = SLIDES.reduce((sum, _, i) => sum + (textWidths[i] || FALLBACK_WIDTH) + GAP, 0);
    if (containerWidth === 0 || singleRowWidth === 0) return;
    setOffset(-singleRowWidth);
  }, [containerWidth, textWidths]);

  // Animation loop for the row
  useEffect(() => {
    if (containerWidth === 0) return;
    let raf;
    let last = performance.now();

    function animate(now) {
      setOffset(prevOffset => {
        const singleRowWidth = SLIDES.reduce((sum, _, i) => sum + (textWidths[i] || FALLBACK_WIDTH) + GAP, 0);
        let nextOffset = prevOffset + SPEED * (now - last);
        if (nextOffset >= 0) {
          nextOffset = -singleRowWidth;
        }
        last = now;
        return nextOffset;
      });
      raf = requestAnimationFrame(animate);
    }
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [containerWidth, textWidths]);

  // Always render the measurement row (hidden but in layout)
  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      mb: 2,
      userSelect: 'none'
    }}>
      {/* Hidden measurement row */}
      <Box sx={{ width: '100%', visibility: 'hidden', height: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          {SLIDES.map((text, i) => (
            <Typography
              key={i}
              ref={textRefs.current[i]}
              variant="h6"
              sx={{
                display: 'inline-block',
                mx: `${GAP / 2}px`,
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
                whiteSpace: 'nowrap'
              }}
            >
              {text}
            </Typography>
          ))}
        </Box>
      </Box>
      {/* Animated visible row */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          overflow: 'hidden',
          height: { xs: 36, sm: 42, md: 48 },
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            transform: `translateX(${offset}px)`,
            transition: 'none'
          }}
        >
          {getFullRowData().map((d, i) => (
            <Typography
              key={d.key + '-dup' + i}
              variant="h6"
              sx={{
                mx: `${GAP / 2}px`,
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
                display: 'inline-block',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                border: '1px solid #1976d2',
                whiteSpace: 'nowrap',
                background: 'rgba(255,255,255,0.92)',
                boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)'
              }}
            >
              {d.text}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TextSlider;
